import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Box } from '~/eds/src/components/Box/Box';
import { Card } from '~/eds/src/components/Card/Card';
import { CollapsibleText } from '~/eds/src/components/CollapsibleText/CollapsibleText';
import { Text } from '~/eds/src/components/Text/Text';
import { UserAction } from '~/eds/src/types/UserAction';
import { copy } from '~/eds/src/utils/copy';
import { ClauseLibraryViewType } from '~/enums/ClauseLibraryViewType';
import { VariationData as VariationDataType } from '~/redux/api/methods/clauseLibrary';
import { slices } from '~/redux/slices';
import { Uuid } from '~/types/Uuid';
import HtmlContent from '~/ui/components/HtmlContent/HtmlContent';
import { htmlToText } from '~/utils/strings';

import { CLAUSE_LIBRARY_CHAR_DISPLAY_LIMIT } from '../constants';

export const ClauseVariation = ({
  canEdit,
  variation,
  clauseId,
  handleDelete,
}: {
  canEdit: boolean;
  variation: VariationDataType;
  clauseId: Uuid;
  handleDelete: (id: Uuid, name: string, clauseId: Uuid) => void;
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  if (!variation) return <></>;

  const editClauseVariation = () => {
    dispatch(
      slices.clauseLibrary.actions.setVariationForm({
        clauseId,
        variationId: variation.key,
        name: variation.name,
        title: variation.name,
        position: variation.position,
        guidanceNotes: variation.guidance,
        clauseText: variation.text,
      }),
    );
    dispatch(slices.clauseLibrary.actions.setEditMode(true));
    dispatch(
      slices.clauseLibrary.actions.setView(ClauseLibraryViewType.VariationForm),
    );
  };

  const cardActions: UserAction[] = [
    {
      mode: 'icon',
      level: 'tertiary',
      icon: 'clipboard',
      text: intl.formatMessage({
        id: 'ClauseVariation.copyAction',
        defaultMessage: 'Copy',
        description: 'Copy action text',
      }),
      tooltip: intl.formatMessage({
        id: 'ClauseVariation.copyTooltip',
        defaultMessage: 'Copy to Clipboard',
        description: 'Copy to clipboard tooltip',
      }),
      onClick: () => copy(variation.text),
    },
  ];

  const moreCardActions = [
    {
      text: intl.formatMessage({
        id: 'ClauseVariation.editAction',
        defaultMessage: 'Edit',
        description: 'Edit action text',
      }),
      onClick: editClauseVariation,
    },
    {
      text: intl.formatMessage({
        id: 'ClauseVariation.deleteAction2',
        defaultMessage: 'Delete',
        description: 'Delete action text',
      }),
      onClick: () => handleDelete(variation.key, variation.name, clauseId),
    },
  ];

  return (
    <Card
      key={variation.key}
      header={{
        actions: cardActions,
        chips: [
          {
            text: variation.position,
            status: variation.status,
          },
        ],
        id: variation.key,
        moreActions: canEdit ? moreCardActions : undefined,
        title: variation.name,
      }}
      mode="embedded"
      role="listitem"
    >
      {Boolean(variation.guidance?.length) && (
        <Box
          styles={componentStyles.wrap}
          bg="background.quiet"
          borderRadius="m"
          py={2}
          px={3}
          my={3}
          mx={6}
        >
          <CollapsibleText
            text={htmlToText(variation.guidance)}
            fullContent={
              <Text>
                <HtmlContent html={variation.guidance} />
              </Text>
            }
            limit={CLAUSE_LIBRARY_CHAR_DISPLAY_LIMIT}
            mode="icon"
          />
        </Box>
      )}
      {Boolean(variation.text?.length) && (
        <Box styles={componentStyles.wrap} mb={3} px={6}>
          <CollapsibleText
            text={htmlToText(variation.text)}
            fullContent={
              <Text>
                <HtmlContent html={variation.text} />
              </Text>
            }
            limit={CLAUSE_LIBRARY_CHAR_DISPLAY_LIMIT}
            mode="icon"
          />
        </Box>
      )}
    </Card>
  );
};

const componentStyles = {
  header: {
    borderTop: 'border',
    scrollMarginTop: '64px',
  },
  wrap: {
    wordWrap: 'break-word',
    hyphens: 'auto',
  },
};

export default ClauseVariation;
